/**
 * GenerationScheduleSubTab — Configure daily and monthly automated accounting generation.
 * Renders inside AccountingConfigPanel as a sub-tab (after Output Formats).
 *
 * KAN-183: Periodic and Batch Accounting
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { accountingService } from '../../services/api/accountingService';
import AlertModal from '../common/AlertModal';
import type { GenerationSchedule, AccountingConcept } from '../../types/accounting';
import './AccountingConfigPanel.css';

interface GenerationScheduleSubTabProps {
  clientId: string;
}

/** Concepts available for daily runs */
const DAILY_CONCEPTS: AccountingConcept[] = [
  'NEW_TRADE',
  'EXPIRY',
  'MODIFICATION_CANCELLED',
  'UNWIND',
];

/** MtM concepts that are always paired */
const MTM_CONCEPTS: AccountingConcept[] = ['VALUATION', 'VALUATION_REVERSAL'];

/** All concepts available for monthly runs */
const MONTHLY_CONCEPTS: AccountingConcept[] = [
  'NEW_TRADE',
  'EXPIRY',
  'MODIFICATION_CANCELLED',
  'UNWIND',
];

const TIMEZONE_OPTIONS = [
  { value: 'America/Santiago', label: 'Santiago (CLT/CLST)' },
  { value: 'America/Lima', label: 'Lima (PET)' },
  { value: 'America/Bogota', label: 'Bogota (COT)' },
];

const DEFAULT_SCHEDULE: GenerationSchedule = {
  daily: {
    enabled: false,
    concepts: [],
    runTime: '21:00',
    timezone: 'America/Santiago',
  },
  monthly: {
    enabled: false,
    concepts: [],
    timing: 'last_business_day',
    runTime: '21:00',
    timezone: 'America/Santiago',
  },
};

const GenerationScheduleSubTab: React.FC<GenerationScheduleSubTabProps> = ({
  clientId,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canManage =
    user?.permissions?.includes('manage_accounting') ||
    user?.permissions?.includes('*') ||
    user?.role === 'client_admin';

  // Schedule state
  const [schedule, setSchedule] = useState<GenerationSchedule>(DEFAULT_SCHEDULE);
  const [originalSchedule, setOriginalSchedule] = useState<GenerationSchedule>(DEFAULT_SCHEDULE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Alert modal
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'error' | 'info' | 'warning';
    title: string;
    message: string;
  }>({ isOpen: false, type: 'info', title: '', message: '' });

  // ============================================
  // Data Loading
  // ============================================

  const loadSchedule = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const response = await accountingService.getGenerationSchedule(clientId);
      if (response.success && response.data) {
        const data = {
          daily: { ...DEFAULT_SCHEDULE.daily, ...response.data.daily },
          monthly: { ...DEFAULT_SCHEDULE.monthly, ...response.data.monthly },
        };
        setSchedule(data);
        setOriginalSchedule(data);
      }
    } catch (err) {
      console.error('Error loading generation schedule:', err);
      setAlertModal({
        isOpen: true,
        type: 'error',
        title: t('common.error'),
        message: t('accounting.schedule.loadFailed'),
      });
    } finally {
      setLoading(false);
    }
  }, [clientId, t]);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule]);

  // Track changes
  useEffect(() => {
    setHasChanges(JSON.stringify(schedule) !== JSON.stringify(originalSchedule));
  }, [schedule, originalSchedule]);

  // ============================================
  // Handlers
  // ============================================

  const handleSave = async () => {
    // Validate: if enabled, must have at least one concept
    if (schedule.daily.enabled && schedule.daily.concepts.length === 0) {
      setAlertModal({
        isOpen: true,
        type: 'warning',
        title: t('common.warning'),
        message: t('accounting.schedule.noConceptsDaily'),
      });
      return;
    }
    if (schedule.monthly.enabled && schedule.monthly.concepts.length === 0) {
      setAlertModal({
        isOpen: true,
        type: 'warning',
        title: t('common.warning'),
        message: t('accounting.schedule.noConceptsMonthly'),
      });
      return;
    }

    setSaving(true);
    try {
      const response = await accountingService.saveGenerationSchedule(clientId, schedule);
      if (response.success) {
        const saved = {
          daily: { ...DEFAULT_SCHEDULE.daily, ...response.data?.daily },
          monthly: { ...DEFAULT_SCHEDULE.monthly, ...response.data?.monthly },
        };
        setSchedule(saved);
        setOriginalSchedule(saved);
        setAlertModal({
          isOpen: true,
          type: 'success',
          title: t('common.success'),
          message: t('accounting.schedule.saveSuccess'),
        });
      }
    } catch (err) {
      console.error('Error saving generation schedule:', err);
      setAlertModal({
        isOpen: true,
        type: 'error',
        title: t('common.error'),
        message: t('accounting.schedule.saveFailed'),
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleDailyConcept = (concept: AccountingConcept) => {
    setSchedule((prev) => {
      const concepts = prev.daily.concepts.includes(concept)
        ? prev.daily.concepts.filter((c) => c !== concept)
        : [...prev.daily.concepts, concept];
      return { ...prev, daily: { ...prev.daily, concepts } };
    });
  };

  const toggleMonthlyConcept = (concept: AccountingConcept) => {
    setSchedule((prev) => {
      const concepts = prev.monthly.concepts.includes(concept)
        ? prev.monthly.concepts.filter((c) => c !== concept)
        : [...prev.monthly.concepts, concept];
      return { ...prev, monthly: { ...prev.monthly, concepts } };
    });
  };

  const toggleMonthlyMtm = () => {
    setSchedule((prev) => {
      const hasMtm = MTM_CONCEPTS.every((c) => prev.monthly.concepts.includes(c));
      let concepts: AccountingConcept[];
      if (hasMtm) {
        concepts = prev.monthly.concepts.filter((c) => !MTM_CONCEPTS.includes(c));
      } else {
        const withoutMtm = prev.monthly.concepts.filter((c) => !MTM_CONCEPTS.includes(c));
        concepts = [...withoutMtm, ...MTM_CONCEPTS];
      }
      return { ...prev, monthly: { ...prev.monthly, concepts } };
    });
  };

  // ============================================
  // Render
  // ============================================

  if (loading) {
    return (
      <div className="tab-loading">
        <div className="spinner" />
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  const hasMtmMonthly = MTM_CONCEPTS.every((c) => schedule.monthly.concepts.includes(c));

  return (
    <div className="accounting-config-content">
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal((prev) => ({ ...prev, isOpen: false }))}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        {/* Daily Schedule Section */}
        <div className="config-section" style={{ marginBottom: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>
              {t('accounting.schedule.dailyTitle')}
            </h3>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={schedule.daily.enabled}
                disabled={!canManage}
                onChange={(e) =>
                  setSchedule((prev) => ({
                    ...prev,
                    daily: { ...prev.daily, enabled: e.target.checked },
                  }))
                }
              />
              <span className="slider" />
            </label>
          </div>

          {schedule.daily.enabled && (
            <>
              {/* Concepts */}
              <div className="form-group">
                <label>{t('accounting.schedule.concepts')}</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {DAILY_CONCEPTS.map((concept) => (
                    <label key={concept} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={schedule.daily.concepts.includes(concept)}
                        disabled={!canManage}
                        onChange={() => toggleDailyConcept(concept)}
                      />
                      {t(`accounting.concepts.${concept}`)}
                    </label>
                  ))}
                </div>
              </div>

              {/* Run time */}
              <div className="form-group">
                <label>{t('accounting.schedule.runTime')}</label>
                <input
                  type="time"
                  value={schedule.daily.runTime}
                  disabled={!canManage}
                  onChange={(e) =>
                    setSchedule((prev) => ({
                      ...prev,
                      daily: { ...prev.daily, runTime: e.target.value },
                    }))
                  }
                  style={{ width: '120px' }}
                />
              </div>

              {/* Timezone */}
              <div className="form-group">
                <label>{t('accounting.schedule.timezone')}</label>
                <select
                  value={schedule.daily.timezone}
                  disabled={!canManage}
                  onChange={(e) =>
                    setSchedule((prev) => ({
                      ...prev,
                      daily: { ...prev.daily, timezone: e.target.value },
                    }))
                  }
                >
                  {TIMEZONE_OPTIONS.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
              </div>
              <span className="hint">{t('accounting.schedule.dailyHint')}</span>
            </>
          )}
        </div>

        {/* Monthly Schedule Section */}
        <div className="config-section" style={{ marginBottom: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>
              {t('accounting.schedule.monthlyTitle')}
            </h3>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={schedule.monthly.enabled}
                disabled={!canManage}
                onChange={(e) =>
                  setSchedule((prev) => ({
                    ...prev,
                    monthly: { ...prev.monthly, enabled: e.target.checked },
                  }))
                }
              />
              <span className="slider" />
            </label>
          </div>

          {schedule.monthly.enabled && (
            <>
              {/* Monthly timing — first field so it contextualises everything below */}
              <div className="form-group">
                <label>{t('accounting.schedule.monthlyTiming')}</label>
                <select
                  value={schedule.monthly.timing}
                  disabled={!canManage}
                  onChange={(e) =>
                    setSchedule((prev) => ({
                      ...prev,
                      monthly: {
                        ...prev.monthly,
                        timing: e.target.value as 'last_business_day' | 'first_business_day_next',
                      },
                    }))
                  }
                >
                  <option value="last_business_day">
                    {t('accounting.schedule.timingLastBD')}
                  </option>
                  <option value="first_business_day_next">
                    {t('accounting.schedule.timingFirstBDNext')}
                  </option>
                </select>
                <span className="hint" style={{ marginTop: '0.25rem' }}>
                  {schedule.monthly.timing === 'last_business_day'
                    ? t('accounting.schedule.timingLastBDHelp')
                    : t('accounting.schedule.timingFirstBDNextHelp')}
                </span>
              </div>

              {/* Concepts */}
              <div className="form-group">
                <label>{t('accounting.schedule.concepts')}</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {MONTHLY_CONCEPTS.map((concept) => (
                    <label key={concept} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={schedule.monthly.concepts.includes(concept)}
                        disabled={!canManage}
                        onChange={() => toggleMonthlyConcept(concept)}
                      />
                      {t(`accounting.concepts.${concept}`)}
                    </label>
                  ))}
                  {/* MtM grouped toggle */}
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={hasMtmMonthly}
                      disabled={!canManage}
                      onChange={toggleMonthlyMtm}
                    />
                    {t('accounting.schedule.mtmLabel')}
                  </label>
                </div>
              </div>

              {/* Run time */}
              <div className="form-group">
                <label>{t('accounting.schedule.runTime')}</label>
                <input
                  type="time"
                  value={schedule.monthly.runTime}
                  disabled={!canManage}
                  onChange={(e) =>
                    setSchedule((prev) => ({
                      ...prev,
                      monthly: { ...prev.monthly, runTime: e.target.value },
                    }))
                  }
                  style={{ width: '120px' }}
                />
              </div>

              {/* Timezone */}
              <div className="form-group">
                <label>{t('accounting.schedule.timezone')}</label>
                <select
                  value={schedule.monthly.timezone}
                  disabled={!canManage}
                  onChange={(e) =>
                    setSchedule((prev) => ({
                      ...prev,
                      monthly: { ...prev.monthly, timezone: e.target.value },
                    }))
                  }
                >
                  {TIMEZONE_OPTIONS.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
              </div>
              <span className="hint">{t('accounting.schedule.monthlyHint')}</span>
            </>
          )}
        </div>
      </div>

      {/* Save button */}
      {canManage && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={saving || !hasChanges}
          >
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      )}
    </div>
  );
};

export default GenerationScheduleSubTab;
